// ============================================================================
// Notifications Hook
// Feature: 035-app-shell-enhancement
// Created: 2026-01-21
// Description: Custom hook for managing notifications with polling and state
// User Story: US7 (Notification Center)
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/apiClient';
import { alertSourceLink } from '../utils/alertSourceLink';

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'success';

export interface Notification {
  id: string;
  title: string;
  message: string;
  severity: NotificationSeverity;
  timestamp: Date;
  read: boolean;
  resourceType?: string;
  resourceId?: string;
  resourceUrl?: string;
  // Spec 255: acknowledgement attribution — who acked the alert + when.
  acknowledged?: boolean;
  acknowledgedByName?: string | null;
  acknowledgedAt?: string | null;
}

export interface UseNotificationsOptions {
  /** Polling interval in milliseconds */
  pollInterval?: number;
  /** Maximum number of notifications to keep */
  maxNotifications?: number;
  /** Whether notifications are enabled */
  enabled?: boolean;
}

interface NotificationsResponse {
  notifications: Notification[];
  totalUnread: number;
}

/**
 * Hook for managing notifications with automatic polling
 * 
 * Features:
 * - Automatic polling for new notifications
 * - Read/unread state management
 * - Badge count calculation
 * - Mark all as read functionality
 * - Navigation to notification resources
 */
export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const {
    pollInterval = 30000, // 30 seconds
    maxNotifications = 50,
    enabled = true,
  } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  // Server-side TRUE unacknowledged total (not the count within the fetched window).
  const [totalUnacknowledged, setTotalUnacknowledged] = useState(0);
  
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchNotifications = useCallback(async (): Promise<NotificationsResponse> => {
    const res = await apiClient.get<{
      alerts: Array<{
        id: string;
        severity: 'Critical' | 'Warning' | 'Info';
        title: string;
        capacityName: string | null;
        metricName: string | null;
        createdAt: string;
        isAcknowledgedByMe: boolean;
        acknowledgedAt?: string | null;
        acknowledgedByName?: string | null;
        entityType?: string | null;
        entityId?: string | null;
        resourceType?: string | null;
        sourceScope?: string | null;
      }>;
      unacknowledgedCount: number;
    }>('/api/alerts/recent?limit=20&scope=fleet');

    const alerts = res.data?.alerts ?? [];
    const severityMap: Record<string, NotificationSeverity> = {
      Critical: 'error',
      Warning: 'warning',
      Info: 'info',
    };

    const notifications: Notification[] = alerts.map(a => ({
      id: a.id,
      title: a.title,
      message: [a.capacityName, a.metricName].filter(Boolean).join(' · ') || a.title,
      severity: severityMap[a.severity] ?? 'info',
      // When the alert was raised (dbo.alerts.created_at, ISO string from the API).
      timestamp: new Date(a.createdAt),
      read: a.isAcknowledgedByMe,
      // Spec 255: attribution — who acknowledged + when (independent of the local read flag).
      acknowledged: Boolean(a.acknowledgedAt),
      acknowledgedByName: a.acknowledgedByName ?? null,
      acknowledgedAt: a.acknowledgedAt ?? null,
      resourceType: a.resourceType ?? a.entityType ?? undefined,
      resourceId: a.entityId ?? undefined,
      // Spec 193: deep-link the bell click to the alert's source page (null → not clickable)
      resourceUrl:
        alertSourceLink({
          entityType: a.entityType,
          entityId: a.entityId,
          resourceType: a.resourceType,
          // Spec 265 FR-025b: the stage decides WHICH integrity screen owns the alert.
          sourceScope: a.sourceScope,
        }) ?? undefined,
    }));

    return {
      notifications,
      totalUnread: res.data?.unacknowledgedCount ?? 0,
    };
  }, []);

  /**
   * Load notifications from API
   */
  const loadNotifications = useCallback(async () => {
    if (!enabled) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchNotifications();
      
      // Sort by newest first, on the alert's own createdAt (set in fetchNotifications).
      const notificationsWithDates = response.notifications
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, maxNotifications); // Limit to max notifications

      setNotifications(notificationsWithDates);
      setTotalUnacknowledged(response.totalUnread);
      setLastFetch(new Date());
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        // Silent — transient network failures (e.g. backend restart) should not
        // surface an error state in the bell; stale count is shown until next poll
      }
    } finally {
      setIsLoading(false);
    }
  }, [enabled, fetchNotifications, maxNotifications]);

  /**
   * Mark a notification as read
   */
  // Local dropdown read-flag update only. PERSISTENCE (POST /api/alerts/acknowledge)
  // + the header badge count are owned by useAlerts; NotificationBell coordinates
  // both on click so the badge and the dropdown stay in lock-step (Spec 193).
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
    );
  }, []);

  /**
   * Optimistically flip a notification to acknowledged so the dropdown shows ownership was
   * taken THE INSTANT the Acknowledge button is pressed (the exact actor name is reconciled
   * by the follow-up refresh). No name is guessed here — under "act as", the server records
   * the impersonated user, so guessing would briefly show the wrong person.
   */
  const markAcknowledgedLocal = useCallback((notificationId: string) => {
    const nowIso = new Date().toISOString();
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId
          ? { ...n, read: true, acknowledged: true, acknowledgedAt: n.acknowledgedAt ?? nowIso }
          : n,
      ),
    );
  }, []);

  /**
   * Mark all notifications as read (local dropdown flags; bell persists via useAlerts)
   */
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  /**
   * Remove a notification
   */
  const removeNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  /**
   * Clear all notifications
   */
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  /**
   * Get unread count for badge display
   */
  const unreadCount = notifications.filter(n => !n.read).length;

  /**
   * Get badge display text (shows 99+ for counts > 99)
   */
  const badgeCount = unreadCount > 99 ? '99+' : unreadCount.toString();

  /**
   * Set up polling
   */
  useEffect(() => {
    if (!enabled) {
      // Clear polling if disabled
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      return;
    }

    // Initial load
    loadNotifications();

    // Set up polling
    const poll = () => {
      pollTimeoutRef.current = setTimeout(() => {
        loadNotifications();
        poll(); // Schedule next poll
      }, pollInterval);
    };

    poll();

    // Refresh the dropdown list the moment the tab regains focus, so an acknowledgement
    // made by another user is reflected immediately on return (not up to pollInterval later).
    const refreshOnFocus = (): void => {
      if (document.visibilityState === 'visible') loadNotifications();
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [enabled, pollInterval, loadNotifications]);

  /**
   * Refresh notifications immediately
   */
  const refresh = useCallback(() => {
    loadNotifications();
  }, [loadNotifications]);

  return {
    // State
    notifications,
    unreadCount,
    badgeCount,
    totalUnacknowledged,
    isLoading,
    error,
    lastFetch,

    // Actions
    markAsRead,
    markAcknowledgedLocal,
    markAllAsRead,
    removeNotification,
    clearAll,
    refresh,
  };
};

export default useNotifications;